import React, { FC, Fragment, Suspense, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Box, FormControl, SideSheetContentCol, Spinner, SpinnerSize, TextArea, TextFieldSize } from '@bechtel/gds';
import { AttachmentMetadataInput, DeleteBlockAttachmentInput, DeleteBlockAttachmentPayload } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { ErrorMessage, Field, FieldArray } from 'formik';
import produce from 'immer';
import { get, map } from 'lodash';
import { FileUpload, FileUploadInput, FileUploadPreviewImg, FileUploadResponse, SetFile } from '@bechtel/client-common';
import { DELETE_BLOCK_MEDIA } from '@bechtel/client-gql';
import { BlockHeadingStyled, FieldError } from '../styled';
import { BlockTypes as BlockTypeList } from './types-list';
import { MilestoneList } from './milestones-list';
import { useLingui } from '@lingui/react';
import { CreateSingleBlockProps } from './types';

export const CreateSingleBlock: FC<CreateSingleBlockProps> = ({
  milestones,
  selectedMilestone,
  onSetFiles,
  showMilestonesList,
}) => {
  const { i18n } = useLingui();
  let newSetFiles: SetFile | undefined = undefined;

  useEffect(() => {
    if (newSetFiles && onSetFiles) onSetFiles(newSetFiles);
  }, [newSetFiles, onSetFiles]);

  const [deleteBlockMedia, { loading: deleteLoading, data }] = useMutation<
    { deleteBlockAttachment: DeleteBlockAttachmentPayload },
    { input: DeleteBlockAttachmentInput }
  >(DELETE_BLOCK_MEDIA);
  const deleteError = get(data, 'deleteBlockAttachment.error', null);
  return (
    <SideSheetContentCol>
      <BlockHeadingStyled>
        <Trans>Block Type</Trans>
      </BlockHeadingStyled>
      <Box flexDirection={{ sm: 'column' }}>
        <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
          <Field name="blockTypeId" component={BlockTypeList} />
        </Suspense>
        <ErrorMessage name="blockTypeId">
          {(msg) => {
            return (
              <Box p={{ sm: '500' }}>
                <FieldError>{i18n._(msg)}</FieldError>
              </Box>
            );
          }}
        </ErrorMessage>
      </Box>
      {!showMilestonesList && (
        <>
          <BlockHeadingStyled>
            <Trans>Milestone</Trans>
          </BlockHeadingStyled>
          <Box flexDirection={{ sm: 'column' }} css={selectedMilestone ? 'pointer-events:none; opacity:0.5' : ''}>
            <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
              <FieldArray name="milestoneIds">
                {(arrayHelpers) => {
                  return <MilestoneList {...arrayHelpers} milestones={milestones} />;
                }}
              </FieldArray>
            </Suspense>
            <ErrorMessage name="milestoneIds">
              {(msg) => {
                return (
                  <Box p={{ sm: '500' }}>
                    <FieldError>{i18n._(msg)}</FieldError>
                  </Box>
                );
              }}
            </ErrorMessage>
          </Box>
        </>
      )}

      <BlockHeadingStyled>
        <Trans>Comment</Trans>
      </BlockHeadingStyled>
      <Field name="comments">
        {({ field }) => {
          return (
            <Box px={{ sm: '500' }} py={{ sm: '600' }} flexDirection={{ sm: 'column' }}>
              <FormControl label={t`Add a comment (Required)`}>
                <TextArea
                  maxLength={2000}
                  data-testid="comment-input"
                  size={TextFieldSize.LARGE}
                  rows="5"
                  {...field}
                ></TextArea>
              </FormControl>
            </Box>
          );
        }}
      </Field>
      <ErrorMessage name="comments">
        {(msg) => {
          return (
            <Box p={{ sm: '500' }} pt={{ sm: '000' }}>
              <FieldError>{i18n._(msg)}</FieldError>
            </Box>
          );
        }}
      </ErrorMessage>

      <FieldArray name="mediaMetadata">
        {({ form, remove, push }) => {
          return (
            <Fragment>
              <BlockHeadingStyled>
                <Trans>Add a Photo</Trans> ({form.values.mediaMetadata.length}/3)
              </BlockHeadingStyled>
              <Box px={{ sm: '500' }} py={{ sm: '600' }} flexDirection={{ sm: 'column' }}>
                <ErrorMessage name="mediaMetadata">
                  {(msg) => {
                    return (
                      <Box px={{ sm: '500' }} pb={{ sm: '550' }}>
                        <FieldError>{i18n._(msg)}</FieldError>
                      </Box>
                    );
                  }}
                </ErrorMessage>
                <FormControl label={t`Upload a Photo`}>
                  <FileUpload
                    maxFileSizeMB={10}
                    maxFiles={3}
                    filesCount={form.values.mediaMetadata.length ? form.values.mediaMetadata.length : 0}
                    acceptFileTypes="image/jpeg, image/png"
                    onSuccess={(res: FileUploadResponse, file) => {
                      const newMediaData: AttachmentMetadataInput = {
                        fileName: file.name,
                        fileSize: file.size,
                        attachmentId: res.attachmentId,
                        mimeType: file.type,
                      };
                      push(newMediaData);
                    }}
                  >
                    {({ getInputProps, getRootProps, loading, files, setFiles, acceptFileTypes }) => {
                      newSetFiles = setFiles;
                      return (
                        <Fragment>
                          <FileUploadInput
                            getInputProps={getInputProps}
                            getRootProps={getRootProps}
                            loading={loading || deleteLoading}
                            acceptFileTypes={acceptFileTypes}
                          />
                          {deleteError && (
                            <Box my={{ sm: '200' }}>
                              <Trans>There was an error when deleting a file.</Trans>
                            </Box>
                          )}
                          {map(form.values.mediaMetadata, (file, index: number) => {
                            return (
                              <Box mb={{ sm: '400' }} key={`${file.name}.${index}`} flexDirection={{ sm: 'column' }}>
                                <FileUploadPreviewImg
                                  file={files[index]}
                                  onDelete={async () => {
                                    const { data } = await deleteBlockMedia({
                                      variables: {
                                        input: { attachmentId: files[index].attachmentId },
                                      },
                                    });
                                    if (data && !data.deleteBlockAttachment.error) {
                                      remove(index);
                                      setFiles(
                                        produce((draft) => {
                                          //@ts-ignore splice will exist on draft since it is an array
                                          draft.splice(index, 1);
                                        })
                                      );
                                    }
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Fragment>
                      );
                    }}
                  </FileUpload>
                </FormControl>
              </Box>
            </Fragment>
          );
        }}
      </FieldArray>
    </SideSheetContentCol>
  );
};
